import { useToast } from "@/hooks/use-toast";
import {
  InternalUsersService,
  type UpdateInternalProfileRequest,
  type UpdateInternalProfileResponse,
} from "@/lib/api/internalUsers.service";
import { useAuth } from "@/lib/auth";
import { useMutation } from "@tanstack/react-query";

const EMAIL_CONFLICT_HINT = "already in use";

export function useUpdateInternalProfile() {
  const { refreshUser } = useAuth();
  const { toast } = useToast();

  return useMutation<
    UpdateInternalProfileResponse,
    Error,
    UpdateInternalProfileRequest
  >({
    mutationFn: (data) => InternalUsersService.updateInternalProfile(data),
    onSuccess: async () => {
      // Both Settings and My Profile read the user from AuthContext; a stale
      // email there would be sent back (and restored) on the next save.
      await refreshUser();
      toast({ title: "Profile updated" });
    },
    onError: (err) => {
      const isEmailConflict = err.message
        .toLowerCase()
        .includes(EMAIL_CONFLICT_HINT);
      toast({
        title: isEmailConflict
          ? "Email already in use"
          : "Couldn’t update profile",
        description: isEmailConflict ? undefined : err.message,
        variant: "destructive",
      });
    },
  });
}
